import React, { useState, useEffect, useMemo } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  trackQuickstartPackDownload,
  trackSampleStartAgain,
  trackAnalysisFinish,
  trackGenerationFinish,
} from '../../../analytics';

import { SellingPoint } from '../../../components/custom';
import { Button } from '../../../components/general';
import { PageWrapper } from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { URL, ZIP_URL, UG_ID } from '../../../constants';

import useSampleProjectData from '../../../hooks/useSampleProjectData';
import useVbData from '../../../hooks/useVbData';

import { getCreationResults } from '../../../services/api/creations';
import {
  getAnalysisData,
  getReviewData,
} from '../../../services/videoBuilderService';

import { useProjectStore } from '../../../stores/project';
import { useVideoBuilderStore } from '../../../stores/videoBuilder';

import { Person, HlReel, Shot, AnalysisClips, PageError } from '../../../types';
import { isEmpty, getUrl, getSampleSelected } from '../../../utils';

import {
  getClipSelections,
  getPersonWildcardsData,
} from '../components/getAnalysisData';
import CreateStep from './components/createStep';
import ProjectName from './components/projectName';
import SelectStep from './components/selectStep';
import SetDateStep from './components/setDateStep';
import ShareStep from './components/shareStep';
import WaitStep from './components/waitStep';

const VideoBuilder = () => {
  const { eventId = '' } = useParams();

  const {
    eventId: eventIdLocal,
    currentProject: currentProjectLocal,
    projectError,
    setEventId,
    setSampleProject,
  } = useProjectStore();
  const { clipSelections, setClipSelections } = useVideoBuilderStore();

  // upload
  const uploadData = JSON.parse(localStorage.getItem('uploadData') ?? '{}');

  const [isPeopleUploaded, setIsPeopleUploaded] = useState(false);
  const [peopleCount, setPeopleCount] = useState(0);

  const [isHlReelUploaded, setIsHlReelUploaded] = useState(false);

  const [isClipsUploaded, setIsClipsUploaded] = useState(false);
  const [clipsCount, setClipsCount] = useState(0);

  // analysis
  const [isAnalysing, setIsAnalysing] = useState(false);

  const [people, setPeople] = useState<Person[]>([]);

  const [hlReel, setHlReel] = useState<HlReel | null>(null);
  const [requiredShots, setRequiredShots] = useState<Shot[]>([]);

  const [analysisClips, setAnalysisClips] = useState<AnalysisClips | null>(
    null,
  );

  // generation
  const [generatedPeopleIds, setGeneratedPeopleIds] = useState<string[]>([]);

  // general
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  // sample project data
  const {
    isSample,
    sampleProjectData,
    updateSampleProjectData,
    resetSampleProjectData,
  } = useSampleProjectData(eventId);

  const { toGeneratePeopleIds, updateToGeneratePeopleIds } = useVbData();

  const currentProject = isSample
    ? sampleProjectData.event
    : currentProjectLocal;

  const isAttendeeReview = currentProject?.attendeeReviewData?.isAttendeeReview;

  const navigate = useNavigate();

  // clip selections – find the matching clips for each person's wildcard spot
  useEffect(() => {
    if (isLoading || isAnalysing) return;

    const clipSelectionsTemp = getClipSelections({ people, requiredShots });

    setClipSelections(clipSelectionsTemp);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAnalysing, people, requiredShots]);

  // clip groups – get complete people, incomplete people from most filled to least, and generated people
  const { completePeople, incompletePeople, generatedPeople } = useMemo(() => {
    let completePeople: Person[] = [];
    let incompletePeople: Person[] = [];
    let generatedPeople: Person[] = [];

    if (isEmpty(people) || isEmpty(clipSelections))
      return { completePeople, incompletePeople, generatedPeople };

    people.forEach((person) => {
      const { missingClips, isEnoughClips } = getPersonWildcardsData(
        person.face.elementId,
        requiredShots,
        clipSelections,
      );

      person.face.missingClips = missingClips;

      if (generatedPeopleIds.includes(person.face.elementId))
        generatedPeople.push(person);
      else if (isEnoughClips) completePeople.push(person);
      else incompletePeople.push(person);
    });

    incompletePeople.sort(
      (one, other) =>
        (one.face.missingClips?.length ?? 0) -
        (other.face.missingClips?.length ?? 0),
    );

    return { completePeople, incompletePeople, generatedPeople };
  }, [people, clipSelections, generatedPeopleIds, requiredShots]);

  const isAssetsUploaded = useMemo(() => {
    localStorage.setItem(
      'uploadData',
      JSON.stringify({
        eventId,
        peopleCount,
        isHlReelUploaded,
        clipsCount,
        rejectedAnalysis:
          uploadData?.eventId === eventId ? uploadData.rejectedAnalysis : false,
      }),
    );

    return isPeopleUploaded && isHlReelUploaded && isClipsUploaded;
  }, [eventId, isPeopleUploaded, isClipsUploaded, isHlReelUploaded]);

  const isUploadStage = currentProject?.stage === 'upload';
  const isAnalysisStage = currentProject?.stage === 'analysis';
  const isGenerationStage = currentProject?.stage === 'generation';

  const isAssetsProcessed =
    !isUploadStage &&
    ((!isSample && isAssetsUploaded && !isAnalysing) ||
      (isSample && sampleProjectData.isAssetsProcessed));

  const isVideosGenerated = useMemo(() => {
    if (isLoading) return false;

    if (isSample) return sampleProjectData.isVideosGenerated ?? false;

    if (!isGenerationStage) return false;

    if (isEmpty(toGeneratePeopleIds)) return true;

    const generatedPeopleIds = generatedPeople.map((p) => p.face.elementId);
    const completePeopleIds = completePeople.map((p) => p.face.elementId);
    const generatingPeopleIds = toGeneratePeopleIds.filter(
      (id: string) =>
        completePeopleIds.includes(id) && !generatedPeopleIds.includes(id),
    );

    if (isEmpty(generatingPeopleIds)) {
      updateToGeneratePeopleIds([]);
      return true;
    }

    return false;
  }, [
    isLoading,
    isSample,
    sampleProjectData.isVideosGenerated,
    isGenerationStage,
    generatedPeople,
    toGeneratePeopleIds,
    updateToGeneratePeopleIds,
    completePeople,
  ]);

  // when page loads – reset loading & errors states
  const resetPageStates = () => {
    setIsLoading(true);
    setPageError(null);
    setError('');
  };

  // set event id on mount
  useEffect(() => {
    if (isSample) setSampleProject(eventId);
    else if (eventIdLocal !== eventId) setEventId(eventId);
  }, [eventId, eventIdLocal, isSample, setSampleProject, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      resetPageStates();

      await fetchUploadAnalysisData();
      await fetchGenerationData();

      setIsLoading(false);
    };

    const onSampleMount = async () => {
      resetPageStates();

      fetchSampleUploadData();

      if (sampleProjectData.isAssetsProcessed) await fetchSampleReviewData();

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
    } else if (currentProject?.eventId) isSample ? onSampleMount() : onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.eventId, projectError]);

  // if analysing – re-fetch every 10 seconds
  useEffect(() => {
    if (isAnalysisStage && isAnalysing && !isSample) {
      const analysisInterval = setInterval(async () => {
        await fetchUploadAnalysisData();
      }, 10000);

      return () => clearInterval(analysisInterval);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalysing, isAnalysisStage, isSample]);

  // if videos are generating – re-fetch every 10 seconds
  useEffect(() => {
    if (isGenerationStage && !isVideosGenerated && !isSample) {
      const generationInterval = setInterval(async () => {
        await fetchGenerationData();
      }, 10000);

      return () => clearInterval(generationInterval);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isGenerationStage, isVideosGenerated, isSample]);

  // when analysis finish – add GA event & navigate to Review page
  useEffect(() => {
    if (isAnalysisStage && isAssetsProcessed && !isLoading) {
      const projectsUpd = JSON.parse(
        localStorage.getItem('analysedProjects') ?? '[]',
      );

      // check if eventId is not in localStorage analysedProjects – to avoid duplicate events
      if (!projectsUpd.includes(eventId)) {
        trackAnalysisFinish(isSample);

        projectsUpd.push(eventId);
        localStorage.setItem('analysedProjects', JSON.stringify(projectsUpd));

        if (!isAttendeeReview) navigate(getUrl(URL.VB_REVIEW, eventId));
      }
    }
  }, [currentProject, eventId, isAssetsProcessed]);

  // when generation finish – add GA event & navigate to Share page
  useEffect(() => {
    if (isGenerationStage && isVideosGenerated && !isLoading) {
      const projectsUpd = JSON.parse(
        localStorage.getItem('generatedProjects') ?? '[]',
      );

      // check if eventId is not in localStorage generatedProjects – to avoid duplicate events
      if (!projectsUpd.includes(eventId)) {
        trackGenerationFinish(isSample);

        projectsUpd.push(eventId);
        localStorage.setItem('generatedProjects', JSON.stringify(projectsUpd));

        navigate(getUrl(URL.VB_SHARE, eventId));
      }
    }
  }, [currentProject, eventId, isVideosGenerated]);

  const fetchUploadAnalysisData = async () => {
    try {
      const { peopleCount, hlReel, clips, clipsCount, isAnalysed } =
        await getAnalysisData({ eventId });

      // UPLOAD
      setIsPeopleUploaded(peopleCount > 0);
      setPeopleCount(peopleCount);

      setIsHlReelUploaded(!!hlReel);

      setIsClipsUploaded(clipsCount > 0);
      setClipsCount(clipsCount);

      if (isUploadStage) return;

      // ANALYSIS
      setIsAnalysing(!isAnalysed);
      setAnalysisClips(clips);

      await fetchReviewData();
    } catch (err) {
      console.error(`Error fetching upload & analysis data: ${err}`);
      setError('Error fetching upload & analysis data');
    }
  };

  const fetchReviewData = async () => {
    try {
      const { people, hlReel, requiredShots } = await getReviewData({
        eventId,
      });

      setPeople(people);
      setHlReel(hlReel);
      setRequiredShots(requiredShots);
    } catch (err) {
      console.error(`Error fetching review data: ${err}`);
      setError('Error fetching review data');
    }
  };

  const fetchGenerationData = async () => {
    try {
      const generatedPeople = await getCreationResults(eventId);

      setGeneratedPeopleIds(
        generatedPeople.map((person) => person.face.elementId),
      );
    } catch (err) {
      console.error(`Error fetching generation data: ${err}`);
      setError('Error fetching generation data');
    }
  };

  const fetchSampleUploadData = (customPeople?: Person[]) => {
    // people
    const selectedPeople: Person[] = getSampleSelected(
      customPeople ?? sampleProjectData.people,
    );
    const generatedPeopleIds = selectedPeople
      .filter((person) => person.isGenerated)
      .map((person) => person.face.elementId);

    setIsPeopleUploaded(sampleProjectData.isPeopleUploaded);
    setPeopleCount(selectedPeople.length);
    setGeneratedPeopleIds(generatedPeopleIds);

    // highlight reel
    setIsHlReelUploaded(sampleProjectData.isHlReelUploaded);
    setHlReel(sampleProjectData.hlReel);

    // clips
    const sampleClips = getSampleSelected(sampleProjectData.clips);

    setIsClipsUploaded(sampleProjectData.isClipsUploaded);
    setClipsCount(sampleClips.length);
  };

  const fetchSampleReviewData = async () => {
    try {
      const id = sampleProjectData.event.eventId;

      const { people, requiredShots } = await getReviewData({
        eventId: id,
        isSample: true,
      });

      setPeople(people);
      setRequiredShots(requiredShots);
    } catch (err) {
      console.error(`Error fetching the analysis data: ${err}`);
      setError('Error fetching the analysis data');
    }
  };

  const handleSampleAnalysisActions = async (
    newSampleData: any,
    isAfter?: boolean,
  ) => {
    updateSampleProjectData(newSampleData);

    if (isAfter) fetchSampleReviewData();
  };

  const handleSampleGenerationActions = (newSampleData: any) => {
    updateSampleProjectData(newSampleData);

    fetchSampleUploadData(newSampleData.people);
  };

  const resetSample = () => {
    resetSampleProjectData();
    window.location.reload();
    trackSampleStartAgain('Video Builder');
  };

  const triggerUG = () => {
    sessionStorage.setItem('showSampleUG', 'true');
    window.userGuiding.previewGuide(UG_ID.SAMPLE_P1);
  };

  const content = (
    <React.Fragment>
      <SelectStep
        peopleCount={peopleCount}
        clipsCount={clipsCount}
        isPeopleUploaded={isPeopleUploaded}
        isHlReelUploaded={isHlReelUploaded}
        isClipsUploaded={isClipsUploaded}
      />

      <CreateStep
        isDisabled={!isAssetsUploaded}
        uploadedPeopleCount={peopleCount}
        uploadedClipsCount={clipsCount}
        hlReel={hlReel}
        analysisClips={analysisClips}
        analyseStatus={
          isAssetsProcessed
            ? 'analysed'
            : isAnalysisStage
              ? 'analysing'
              : 'not-analysed'
        }
        onAnalysisStart={() => setIsAnalysing(true)}
        requiredShots={requiredShots}
        completePeople={completePeople}
        incompletePeople={incompletePeople}
        generatedPeople={generatedPeople}
        isGenerationStage={isGenerationStage}
        handleSampleAnalysisActions={handleSampleAnalysisActions}
        handleSampleGenerationActions={handleSampleGenerationActions}
      />

      <ShareStep peopleCount={generatedPeople.length} />
    </React.Fragment>
  );

  const arContent = (
    <React.Fragment>
      <SetDateStep />

      <SelectStep
        peopleCount={peopleCount}
        clipsCount={clipsCount}
        isPeopleUploaded={isPeopleUploaded}
        isHlReelUploaded={isHlReelUploaded}
        isClipsUploaded={isClipsUploaded}
      />

      <WaitStep
        isAssetsUploaded={isAssetsUploaded}
        isVideosGenerated={!isEmpty(generatedPeople)}
      />

      <ShareStep peopleCount={generatedPeople.length} />
    </React.Fragment>
  );

  return (
    <PageWrapper
      type="project"
      title={`Video Builder – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
      className="items-center gap-8"
    >
      <div className="w-full sm:w-1/2 flex flex-col items-center gap-2 text-center">
        <ProjectName
          projectName={currentProject?.name}
          eventId={eventId}
          isSample={isSample}
          resetSample={resetSample}
        />
      </div>

      {isMobile || isTablet ? (
        <SellingPoint
          variant="pink"
          text="CrowdClip&reg; is not optimised for mobile devices. For the best experience, use on a desktop."
          icon={<></>}
          className="w-full sm:w-1/2 justify-center"
        />
      ) : (
        !isPeopleUploaded &&
        !isHlReelUploaded &&
        !isClipsUploaded && (
          <SellingPoint
            variant="grey-outline"
            text={
              isSample ? (
                <>
                  Ready to get started? Let's create a sample video project by
                  going through the steps below and with the assets provided.{' '}
                  <Button variant="link" onClick={triggerUG}>
                    Walk me through it
                  </Button>
                </>
              ) : (
                <>
                  First time to use CrowdClip&reg;? Download some sample files
                  to test with.
                  <br />
                  <Link
                    to={ZIP_URL.QUICKSTART}
                    onClick={() => trackQuickstartPackDownload('Video Builder')}
                    className="text-black"
                  >
                    Download the quickstart pack
                  </Link>
                </>
              )
            }
            icon={<></>}
            className="w-full justify-center"
          />
        )
      )}

      <div className="w-full flex flex-col gap-4">
        {isAttendeeReview ? arContent : content}
      </div>

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default VideoBuilder;
